"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { useCalendarDefaults, type HourCycle } from "./calendar";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "./input-group";
import { ToggleGroup, ToggleGroupItem } from "./toggle-group";

export type TimeFieldValue = { hours: number; minutes: number };

export type TimeFieldProps = Omittable<{
  /** The clock being edited: the moment's hours and minutes, or null for
   *  no time yet. */
  value: TimeFieldValue | null;
  /** Fires only with a complete, valid clock — a half-typed hour never
   *  reaches the caller. */
  onValueChange: (next: TimeFieldValue) => void;
  /** `12` shows 1–12 plus an AM/PM switch; `24` shows 00–23. Defaults to
   *  the ambient `CalendarDefaultsProvider` clock, else 12. */
  hourCycle?: HourCycle;
  /** Names the whole field ("Time", "Start time") for assistive tech. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Focuses the hour on mount — for a popup whose only job is this. */
  autoFocus?: boolean;
  className?: string;
}>;

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** 24h hours → the hour shown on a 12h clock (12, 1–11). */
function to12(hours: number): number {
  const h = hours % 12;
  return h === 0 ? 12 : h;
}

/** A 12h hour + period → 24h hours. */
function from12(hour12: number, pm: boolean): number {
  const base = hour12 % 12;
  return pm ? base + 12 : base;
}

/**
 * A typed clock: an hour box, a minute box and, on a 12-hour clock, an
 * AM/PM switch. It replaces the browser's own time control, whose period
 * is a faint suffix and whose picker affordance cannot be styled.
 *
 * Typing: digits only; a box commits when it holds two digits or loses
 * focus; ↑/↓ step it. Each box keeps a draft while focused so a backspace
 * mid-edit does not write a wrong time — only a complete, in-range clock
 * calls `onValueChange`.
 */
export function TimeField({
  value,
  onValueChange,
  hourCycle: hourCycleProp,
  ariaLabel,
  disabled,
  autoFocus,
  className,
}: TimeFieldProps): React.ReactElement {
  const defaults = useCalendarDefaults();
  const hourCycle: HourCycle = hourCycleProp ?? defaults.hourCycle ?? 12;
  const twelve = hourCycle === 12;

  const shownHour =
    value == null ? "" : twelve ? String(to12(value.hours)) : pad2(value.hours);
  const shownMinute = value == null ? "" : pad2(value.minutes);
  const pm = value != null && value.hours >= 12;

  // Drafts live only while a box is focused; null means "show the value".
  // Each is mirrored in a ref because a commit moves focus SYNCHRONOUSLY
  // inside the change handler, and the blur that fires there would read the
  // previous render's draft — the half-typed hour — and commit it.
  const [hourDraft, setHourDraft] = React.useState<string | null>(null);
  const [minuteDraft, setMinuteDraft] = React.useState<string | null>(null);
  const hourDraftRef = React.useRef<string | null>(null);
  const minuteDraftRef = React.useRef<string | null>(null);
  const setHour = (next: string | null) => {
    hourDraftRef.current = next;
    setHourDraft(next);
  };
  const setMinute = (next: string | null) => {
    minuteDraftRef.current = next;
    setMinuteDraft(next);
  };
  const minuteRef = React.useRef<HTMLInputElement>(null);

  const maxHour = twelve ? 12 : 23;
  const minHour = twelve ? 1 : 0;

  const commit = React.useCallback(
    (hours: number, minutes: number) => {
      if (
        !Number.isInteger(hours) ||
        !Number.isInteger(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return;
      }
      if (value && value.hours === hours && value.minutes === minutes) return;
      onValueChange({ hours, minutes });
    },
    [onValueChange, value],
  );

  const commitHour = React.useCallback(
    (raw: string) => {
      if (raw === "") return;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < minHour || n > maxHour) return;
      const hours = twelve ? from12(n, pm) : n;
      commit(hours, value?.minutes ?? 0);
    },
    [commit, maxHour, minHour, pm, twelve, value?.minutes],
  );

  const commitMinute = React.useCallback(
    (raw: string) => {
      if (raw === "") return;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0 || n > 59) return;
      commit(value?.hours ?? 0, n);
    },
    [commit, value?.hours],
  );

  const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 2);
    // Two digits — or one that no second digit could extend in range — is a
    // finished hour: commit, drop the draft and hand focus to the minutes.
    const n = Number(digits);
    const done =
      digits.length === 2 || (digits.length === 1 && n * 10 > maxHour);
    if (done && n >= minHour && n <= maxHour) {
      commitHour(digits);
      setHour(null);
      minuteRef.current?.focus();
      minuteRef.current?.select();
      return;
    }
    setHour(digits);
  };

  const handleMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 2);
    if (digits.length === 2) {
      commitMinute(digits);
      setMinute(null);
      return;
    }
    setMinute(digits);
  };

  const step = (
    event: React.KeyboardEvent<HTMLInputElement>,
    part: "hours" | "minutes",
  ) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    event.preventDefault();
    const delta = event.key === "ArrowUp" ? 1 : -1;
    const hours = value?.hours ?? (twelve ? 12 : 0);
    const minutes = value?.minutes ?? 0;
    if (part === "minutes") {
      commit(hours, (minutes + delta + 60) % 60);
    } else if (twelve) {
      // Step within the period: 11 AM → 12 AM, never across to PM.
      const next = ((to12(hours) - 1 + delta + 12) % 12) + 1;
      commit(from12(next, pm), minutes);
    } else {
      commit((hours + delta + 24) % 24, minutes);
    }
  };

  const setPeriod = (nextPm: boolean) => {
    const hours = value?.hours ?? (nextPm ? 12 : 0);
    commit(from12(to12(hours), nextPm), value?.minutes ?? 0);
  };

  return (
    <InputGroup
      aria-label={ariaLabel}
      // Hugs its boxes instead of the row: two digit cells, a colon, the
      // period. `Input` wraps its element in a `contents` control, so the
      // cell geometry is set on the inner input from here.
      className={cn(
        "w-auto self-start ps-[calc(--spacing(3)-1px)]",
        twelve ? "pe-1" : "pe-[calc(--spacing(3)-1px)]",
        "**:[input]:w-8 **:[input]:min-w-0 **:[input]:px-0 **:[input]:text-center **:[input]:tabular-nums **:[input]:placeholder:text-muted-foreground/48",
        className,
      )}
      data-hour-cycle={hourCycle}
      data-slot="time-field"
    >
      <InputGroupInput
        aria-label="Hour"
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        inputMode="numeric"
        onBlur={() => {
          const draft = hourDraftRef.current;
          if (draft != null) commitHour(draft);
          setHour(null);
        }}
        onChange={handleHourChange}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => step(event, "hours")}
        placeholder="--"
        value={hourDraft ?? shownHour}
      />
      <InputGroupText aria-hidden className="px-0.5 text-muted-foreground">
        :
      </InputGroupText>
      <InputGroupInput
        aria-label="Minute"
        autoComplete="off"
        disabled={disabled}
        inputMode="numeric"
        onBlur={() => {
          const draft = minuteDraftRef.current;
          if (draft != null) commitMinute(draft);
          setMinute(null);
        }}
        onChange={handleMinuteChange}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => step(event, "minutes")}
        placeholder="--"
        ref={minuteRef}
        value={minuteDraft ?? shownMinute}
      />
      {twelve ? (
        <InputGroupAddon align="inline-end" className="cursor-default pe-0 ps-2">
          {/* The period is a real switch, not a suffix: two xs toggles the
              eye reads at a glance — muted at rest, filled once pressed. */}
          <ToggleGroup
            aria-label="AM or PM"
            disabled={disabled}
            onValueChange={(next: unknown[]) => {
              const picked = next[0];
              if (picked === "am" || picked === "pm") setPeriod(picked === "pm");
            }}
            size="xs"
            value={value == null ? [] : [pm ? "pm" : "am"]}
            variant="outline"
          >
            <ToggleGroupItem aria-label="AM" value="am">
              AM
            </ToggleGroupItem>
            <ToggleGroupItem aria-label="PM" value="pm">
              PM
            </ToggleGroupItem>
          </ToggleGroup>
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
}
